import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { profileApi, baiApi } from '../services/api';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const winPct = (won, played) =>
  played > 0 ? `${Math.round((won / played) * 100)}%` : '—';

const sportLabel = (s) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

const disciplineLabel = (t) => {
  if (!t) return 'Unknown';
  return t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Returns result badge info based on round position in knockout brackets.
// League play has no round-based result — returns null for those.
const getResultLabel = (row) => {
  if (row.draw_type === 'league') return null;
  const maxReached = Number(row.max_round_reached);
  const eventMax = Number(row.event_max_round);
  if (!maxReached || !eventMax) return null;

  const roundsFromFinal = eventMax - maxReached;

  if (roundsFromFinal === 0) {
    // Was in the final — winner has no losses (won every match)
    const allWins = Number(row.matches_played) > 0 && Number(row.matches_won) === Number(row.matches_played);
    if (allWins) return { label: 'Winner', bg: '#fef3c7', color: '#92400e', border: '#fcd34d' };
    return { label: 'Runner-up', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
  }
  if (roundsFromFinal === 1) return { label: 'Semi-finalist', bg: '#CCFBF1', color: '#5b21b6', border: '#c4b5fd' };
  if (roundsFromFinal === 2) return { label: 'Quarter-finalist', bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' };
  if (roundsFromFinal === 3) return { label: 'Round of 16', bg: '#f0fdf4', color: '#166534', border: '#86efac' };
  return { label: `Round ${maxReached}`, bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' };
};

const ResultBadge = ({ row }) => {
  const result = getResultLabel(row);
  if (!result) return null;
  return (
    <span style={{
      fontSize: '0.7rem', padding: '2px 8px', borderRadius: 999, fontWeight: '600',
      background: result.bg, color: result.color, border: `1px solid ${result.border}`,
      whiteSpace: 'nowrap',
    }}>
      {result.label}
    </span>
  );
};

const PlayerProfile = () => {
  const { profileId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [baiRankings, setBaiRankings] = useState(null);   // null = not fetched yet
  const [baiLoading, setBaiLoading] = useState(false);

  useEffect(() => {
    profileApi.getById(profileId)
      .then(res => setProfile(res.data))
      .catch(() => setError('Player profile not found'))
      .finally(() => setLoading(false));
  }, [profileId]);

  // Once profile loads, check for a BAI identifier and fetch rankings
  useEffect(() => {
    if (!profile) return;
    const baiIdentifier = profile.identifiers?.find(id => id.id_type === 'bai_id');
    if (!baiIdentifier) return;

    setBaiLoading(true);
    baiApi.ranking(baiIdentifier.id_value)
      .then(res => setBaiRankings(res.data.rankings || []))
      .catch(() => setBaiRankings([]))
      .finally(() => setBaiLoading(false));
  }, [profile]);

  if (loading) return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );

  if (error || !profile) return (
    <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
      <p style={{ color: 'var(--text-secondary)' }}>{error || 'Profile not found'}</p>
      <Link to="/tournaments" className="btn btn-primary">Browse Tournaments</Link>
    </div>
  );

  const { stats = {}, history = [], identifiers = [] } = profile;
  const byEventType = stats.by_event_type || [];

  const sports = (() => {
    try { return Array.isArray(profile.sports) ? profile.sports : JSON.parse(profile.sports || '[]'); }
    catch { return []; }
  })();

  // Group history rows by tournament
  const byTournament = history.reduce((acc, row) => {
    if (!acc[row.tournament_id]) {
      acc[row.tournament_id] = {
        tournament_id: row.tournament_id,
        tournament_name: row.tournament_name,
        sport_type: row.sport_type,
        start_date: row.start_date,
        draw_type: row.draw_type,
        events: [],
      };
    }
    acc[row.tournament_id].events.push(row);
    return acc;
  }, {});
  const tournaments = Object.values(byTournament).sort((a, b) =>
    new Date(b.start_date) - new Date(a.start_date)
  );

  return (
    <div style={{ maxWidth: '860px', margin: '0 auto', padding: '2rem 1rem' }}>

      {/* ── Profile Header ─────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--primary-color) 0%, #0D9488 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2rem', color: 'white', fontWeight: '700', flexShrink: 0,
          }}>
            {profile.display_name?.charAt(0).toUpperCase() || '?'}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: '0 0 0.3rem', fontSize: '1.5rem', fontWeight: '800' }}>
              {profile.display_name}
            </h1>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {profile.nationality && (
                <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>{profile.nationality}</span>
              )}
              {profile.city && (
                <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                  {[profile.city, profile.state, profile.country].filter(Boolean).join(', ')}
                </span>
              )}
              {profile.gender && (
                <span style={{ fontSize: '0.75rem', padding: '1px 8px', background: '#F0FDFA', color: '#115E59', borderRadius: 999, border: '1px solid #99F6E4' }}>
                  {profile.gender}
                </span>
              )}
              {profile.date_of_birth && (
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  DOB: {fmtDate(profile.date_of_birth)}
                </span>
              )}
            </div>
            {sports.length > 0 && (
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {sports.map(s => (
                  <span key={s} style={{ fontSize: '0.75rem', padding: '2px 8px', background: '#d1fae5', color: '#065f46', borderRadius: 999, border: '1px solid #6ee7b7' }}>
                    {sportLabel(s)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Overall Career Stats ────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Tournaments', value: stats.tournaments_played ?? 0 },
          { label: 'Matches Played', value: stats.matches_played ?? 0 },
          { label: 'Matches Won', value: stats.matches_won ?? 0 },
          { label: 'Win Rate', value: winPct(stats.matches_won, stats.matches_played) },
        ].map(({ label, value }) => (
          <div key={label} className="card" style={{ padding: '1rem', textAlign: 'center', margin: 0 }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '800', color: 'var(--primary-color)' }}>{value}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── By Discipline ───────────────────────────────────────────────────── */}
      {byEventType.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
            By Discipline
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
            {byEventType.map(d => (
              <div key={d.event_type} style={{ padding: '0.75rem 1rem', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <div style={{ fontWeight: '700', fontSize: '0.9rem', marginBottom: '0.5rem', color: '#374151' }}>
                  {disciplineLabel(d.event_type)}
                </div>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: '800', color: 'var(--primary-color)' }}>
                      {d.tournaments_played}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tournaments</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#065f46' }}>
                      {d.matches_won}W
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Won</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#991b1b' }}>
                      {Number(d.matches_played) - Number(d.matches_won)}L
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lost</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#374151' }}>
                      {winPct(d.matches_won, d.matches_played)}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Win%</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Federation IDs ─────────────────────────────────────────────────── */}
      {identifiers.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
            Federation IDs
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {identifiers.map(id => (
              <div key={id.id} style={{ padding: '0.35rem 0.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.85rem' }}>
                <span style={{ fontSize: '0.72rem', color: '#6b7280', marginRight: '0.4rem', textTransform: 'uppercase' }}>{id.id_type}:</span>
                <span style={{ fontWeight: '600', fontFamily: 'monospace' }}>{id.id_value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── BAI Rankings ───────────────────────────────────────────────────── */}
      {(baiLoading || (baiRankings && baiRankings.length > 0)) && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
            BAI Rankings
          </h3>

          {baiLoading ? (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {[1, 2].map(i => (
                <div key={i} className="skeleton" style={{ height: 64, flex: 1, borderRadius: 8 }} />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              {baiRankings.map((r, i) => (
                <div key={i} style={{
                  padding: '0.75rem 1rem', borderRadius: 8, border: '1px solid #e5e7eb',
                  background: '#f8fafc', minWidth: 160,
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.3rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '1.6rem', fontWeight: '800', color: 'var(--primary-color)', lineHeight: 1 }}>
                      #{r.rank}
                    </span>
                    {r.grand_total > 0 && (
                      <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>{r.grand_total} pts</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>{r.category}</div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '2px' }}>
                    {r.age_label}{r.state ? ` · ${r.state}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tournament History ─────────────────────────────────────────────── */}
      <div className="card">
        <h3 style={{ margin: '0 0 1rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
          Tournament History
        </h3>

        {tournaments.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>No tournament history yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {tournaments.map(t => {
              const tWon = t.events.reduce((s, e) => s + Number(e.matches_won || 0), 0);
              const tPlayed = t.events.reduce((s, e) => s + Number(e.matches_played || 0), 0);
              return (
                <div key={t.tournament_id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>

                  {/* Tournament header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: '#f9fafb', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Link
                        to={`/t/${t.tournament_id}`}
                        style={{ fontWeight: '700', color: '#0D9488', textDecoration: 'none', fontSize: '0.95rem' }}
                      >
                        {t.tournament_name}
                      </Link>
                      {t.sport_type && (
                        <span style={{ fontSize: '0.7rem', padding: '1px 7px', background: '#CCFBF1', color: '#115E59', borderRadius: 999 }}>
                          {sportLabel(t.sport_type)}
                        </span>
                      )}
                      {t.draw_type === 'league' && (
                        <span style={{ fontSize: '0.68rem', padding: '1px 7px', background: '#fef9c3', color: '#854d0e', borderRadius: 999, border: '1px solid #fde68a' }}>
                          League
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
                      {t.start_date && <span>{fmtDate(t.start_date)}</span>}
                      {tPlayed > 0 && (
                        <span style={{ fontWeight: '600', color: '#374151' }}>
                          {tWon}W / {tPlayed - tWon}L
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Events in this tournament */}
                  <div style={{ padding: '0.5rem 1rem 0.75rem' }}>
                    {t.events.map((ev, i) => (
                      <div
                        key={ev.player_id || i}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.4rem 0',
                          borderBottom: i < t.events.length - 1 ? '1px solid #f3f4f6' : 'none',
                          flexWrap: 'wrap',
                        }}
                      >
                        {/* Event name / discipline */}
                        <span style={{ fontSize: '0.82rem', fontWeight: '500', flex: 1, minWidth: 120 }}>
                          {ev.event_name || [ev.event_type, ev.event_category, ev.age_category].filter(Boolean).join(' · ') || 'General'}
                        </span>

                        {/* Seed */}
                        {ev.seed_number && (
                          <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#92400e' }}>[{ev.seed_number}]</span>
                        )}

                        {/* Affiliation */}
                        {ev.affiliation_name && (
                          <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>{ev.affiliation_name}</span>
                        )}

                        {/* W/L record */}
                        {Number(ev.matches_played) > 0 && (
                          <span style={{
                            fontSize: '0.75rem', padding: '1px 8px',
                            background: '#f3f4f6', color: '#374151',
                            borderRadius: 999, fontWeight: '600',
                            whiteSpace: 'nowrap',
                          }}>
                            {ev.matches_won}W–{Number(ev.matches_played) - Number(ev.matches_won)}L
                          </span>
                        )}

                        {/* Round result badge */}
                        <ResultBadge row={ev} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerProfile;
